import { ValidationControlBase } from "@frui.ts/bootstrap";
import { bound, PropertyName } from "@frui.ts/helpers";
import { IBindingProps } from "@frui.ts/views";
import React from "react";
import { Form } from "react-bootstrap";
import AdvancedInput, { Transformation } from "controls/advancedInput";
import { computed, observable, observe } from "mobx";
import { setVisitedProperty } from "../utils/validationPatch";
import { labeledCountries } from "./licensePlateBaseInput/config";
import classNames from "classnames";
import { renderLabelIcon } from "./licensePlateBaseInput/licensePlateBaseInput";
import { compareStringsWithIndexes } from "validators/licensePlateChangeValidator";

export interface LicensePlateCharacterWiseInputProps {
  id: string;
  translate: (code: string) => string;
  countryCode?: string;
  originalLicensePlate?: string;
  disabled?: boolean;
  errorMessage?: string;
  disableCopy?: boolean;
  disablePaste?: boolean;
  onBlur?: (e: React.FormEvent<any>) => void;
}

interface LicensePlateObject {
  [key: string]: string;
}

export default class LicensePlateCharacterWiseInput<TTarget> extends ValidationControlBase<
  TTarget,
  LicensePlateCharacterWiseInputProps & IBindingProps<TTarget>
> {
  @observable licensePlate: LicensePlateObject;
  ignoredCharacters = new RegExp(/[^A-Z0-9]+/gi);

  @computed
  get valueArray() {
    return Array.from(this.value || "");
  }

  componentDidMount() {
    this.licensePlate = observable(
      this.valueArray.reduce((o, key, index) => Object.assign(o as any, { [index]: key }), {}) as LicensePlateObject
    );

    observe(this.licensePlate, change => {
      const valueArray = this.valueArray;
      if (change.type !== "remove") {
        valueArray[parseInt(change.name, 10)] = change.newValue ? change.newValue : " ";
      } else {
        valueArray[parseInt(change.name, 10)] = " ";
      }

      this.setValue(valueArray.join(""));
    });
  }

  @bound
  protected renderInner() {
    const { id, countryCode, originalLicensePlate, disabled = false, disablePaste, disableCopy, onBlur } = this.props;

    const licensePlate = this.licensePlate;
    const validationError = this.getValidationError();

    const onInputPartBlur = (e: React.FormEvent<any>) => {
      const { target, property } = this.props as { target: TTarget; property: PropertyName<TTarget> };
      setVisitedProperty(target, property);

      if (onBlur) {
        onBlur(e);
      }
    };

    const label = labeledCountries.find(x => x.countryCode === countryCode);
    const labelClass = classNames({
      "country-label": true,
      "character-label": true,
      europe: ["EU", "UA"].includes(label?.labelType ?? ""),
      switzerland: ["CH"].includes(label?.labelType ?? ""),
    });

    let differentCharacterIndexes = [] as number[];
    if (originalLicensePlate) {
      differentCharacterIndexes = compareStringsWithIndexes(originalLicensePlate, this.value);
    }

    function disabledCharacterInput(index: number): boolean {
      if (differentCharacterIndexes.length < 2) {
        return false;
      }
      return !differentCharacterIndexes.includes(index);
    }

    return (
      <>
        <div className="d-flex with-gap license-plate-input">
          {label && (
            <div className={labelClass}>
              {renderLabelIcon(label)}
              <span>{label.labelLetters}</span>
            </div>
          )}
          {this.valueArray.map((character, index) => {
            const classes = classNames({
              "px-1 text-center no-bg": true,
              "bg-light": disabledCharacterInput(index),
            });
            return (
              <div style={{ maxWidth: "3rem" }} key={id + `_${index}`}>
                <AdvancedInput
                  trim={true}
                  ignoredCharacters={this.ignoredCharacters}
                  transformation={Transformation.UpperCased}
                  disabled={disabled}
                  target={licensePlate}
                  property={`${index}`}
                  errorMessage={validationError && !disabledCharacterInput(index) ? " " : undefined}
                  disableCopy={disableCopy}
                  disablePaste={disablePaste}
                  autoComplete="off"
                  maxLength={1}
                  onBlur={onInputPartBlur}
                  className={classes}
                />
              </div>
            );
          })}
        </div>
        {validationError && (
          <Form.Control.Feedback type="invalid" style={{ display: "block" }}>
            {validationError}
          </Form.Control.Feedback>
        )}
      </>
    );
  }
}
